import axiosInstance from '@/libs/axios_config'
import { createPaginatedResponseSchema } from '@/schemas/pagination.schema'
import {
  GetApplicantPaymentsArgs,
  GetPaymentDetailsArgs,
  PaymentDetailSchema,
  PaymentSchema,
  PaymentStatusSchema,
} from '@/schemas/payments.schema'
import { z } from 'zod'
import {
  AppealMarketplacePaymentPayload,
  AppealPaymentPayload,
} from '../providers/types/provider.types'
import { ServiceFeePayerSchema } from './service_fee.schema'
import { parseRequestError } from '@/common/utilities.ts'

/**
 * Retrieves detailed information about a specific payment
 * @param args - The arguments containing payment_id and program_id
 * @returns The parsed payment details
 */
export async function getPaymentDetail(args: GetPaymentDetailsArgs) {
  const { payment_id, program_id, messages } = args

  const response = await axiosInstance.get(`/scholar/payments/${payment_id}`, {
    params: { program_id, ...(messages ? { messages } : {}) },
  })

  return PaymentDetailSchema.parse(response.data)
}

/**
 * Retrieves payments for a specific applicant in a program
 * @param args - The arguments containing program_id, applicant_id, and pagination options
 * @returns The parsed applicant payments response
 */
export async function getPayments(args: GetApplicantPaymentsArgs) {
  const response = await axiosInstance.get('/applicant_payments', {
    params: {
      paginate: 1,
      ...args,
    },
  })

  return createPaginatedResponseSchema(PaymentSchema).parse(response.data)
}

export const PUTAppealPayment = async (payload: AppealPaymentPayload) => {
  const response = await axiosInstance.put(
    `/scholar/payments/${payload.payment_id}`,
    payload
  )

  return response
}

interface PursePayment {
  purse_id: number
  amount: number
}

interface PaymentRequestReviewParams {
  program_id: number
  payment_id: number
  decision: 'accept' | 'reject'
  comment: string
  purse_payments: PursePayment[]
}

const PaymentReviewResponseSchema = z.object({
  id: z.number(),
  status: PaymentStatusSchema,
  type: z.number(),
  program_id: z.number(),
  total_amount: z.number(),
  service_fee: z.number(),
  created_datetime: z.string(),
})

export type PaymentReviewResponse = z.infer<typeof PaymentReviewResponseSchema>

export const reviewPaymentRequest = async (
  params: PaymentRequestReviewParams
) => {
  try {
    const response = await axiosInstance.post(
      '/scholar/payment_request_review',
      params
    )
    return PaymentReviewResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export const acceptOrRejectPayment = async (
  paymentId: number,
  programId: number,
  status: 1 | 2,
  justification?: string
) => {
  const payload = {
    program_id: programId,
    status,
    justification,
  }

  const response = await axiosInstance.put(
    `/scholar/payment_accept_reject/${paymentId}`,
    payload
  )

  return response.data
}

export const AppealPaymentMarketplace = async (
  payload: AppealMarketplacePaymentPayload
) => {
  const response = await axiosInstance.put(
    `/scholar/payments/${payload.payment_id}`,
    payload
  )
  return response
}

const CalculatedFeesSchema = z.object({
  base_amount: z.number(),
  service_fee: z.number(),
  net_amount: z.number(),
  serviceFeePayer: ServiceFeePayerSchema,
})

export type CalculatedFees = z.infer<typeof CalculatedFeesSchema>

export type FeesPayload = {
  base_amount: number
  program_id: number
}
export const calculateFees = async ({
  base_amount,
  program_id,
}: FeesPayload) => {
  const response = await axiosInstance.post(`/payments/calculate_details`, {
    base_amount,
    program_id,
  })

  return CalculatedFeesSchema.parse(response.data)
}
